import { Injectable, inject, signal } from "@angular/core";
import {
  ProviderCaseStatusGetDTO,
  ProviderCaseStatusCreateDTO,
  ProviderCaseStatusUpdateDTO,
  ProviderStatusAppliesTo,
} from "src/app/pages/providers/provider.model";
import { BaseCrudService } from "../generic/base.service";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";
import { isUserOnboardingInProgress } from "src/app/pages/profile/profile.model";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { ToastService } from "src/app/shared/services/toast.service";
import { TranslateService } from "@ngx-translate/core";
import {
  filterProviderStatusesByAppliesTo,
  findProviderStatusByValue,
  normalizeProviderCaseStatuses,
} from "./provider-status.helper";

export {
  DEFAULT_PROVIDER_STATUS_APPLIES_TO,
  filterProviderStatusesByAppliesTo,
  findProviderStatusByValue,
  normalizeProviderCaseStatuses,
  normalizeProviderStatusAppliesTo,
} from "./provider-status.helper";

@Injectable({
  providedIn: "root",
})
export class ProviderCaseStatusService extends BaseCrudService<
  ProviderCaseStatusGetDTO[],
  ProviderCaseStatusCreateDTO,
  ProviderCaseStatusUpdateDTO,
  string
> {
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private profileService = inject(ProfileService);

  providerCaseStatuses = signal<ProviderCaseStatusGetDTO[]>([]);

  clearCache(): void {
    this.providerCaseStatuses.set([]);
  }

  /** Returns cached provider statuses when available; otherwise loads from API. */
  getCachedOrLoad(): Observable<ProviderCaseStatusGetDTO[]> {
    if (this.shouldSkipStatusFetch()) {
      return of([]);
    }

    const cached = this.providerCaseStatuses();
    return cached.length > 0 ? of(cached) : this.get();
  }

  getCachedOrLoadByAppliesTo(
    appliesTo: ProviderStatusAppliesTo,
  ): Observable<ProviderCaseStatusGetDTO[]> {
    return this.getCachedOrLoad().pipe(
      map((statuses) => filterProviderStatusesByAppliesTo(statuses, appliesTo)),
    );
  }

  filterByAppliesTo(
    statuses: ProviderCaseStatusGetDTO[],
    appliesTo: ProviderStatusAppliesTo,
  ): ProviderCaseStatusGetDTO[] {
    return filterProviderStatusesByAppliesTo(statuses, appliesTo);
  }

  findByValue(
    statuses: ProviderCaseStatusGetDTO[],
    value: string,
    appliesTo?: ProviderStatusAppliesTo,
  ): ProviderCaseStatusGetDTO | undefined {
    return findProviderStatusByValue(statuses, value, appliesTo);
  }

  constructor() {
    super("/entity/provider_case_status");
  }

  override get(
    id?: string,
    params?: Record<string, any>,
  ): Observable<ProviderCaseStatusGetDTO[]> {
    if (this.shouldSkipStatusFetch()) {
      return of([]);
    }

    return super.get(id, params).pipe(
      map((response) => normalizeProviderCaseStatuses(response ?? [])),
      tap((response) => {
        this.providerCaseStatuses.set(response);
      }),
      catchError((error) => {
        this.toast.error(
          this.translate.instant("toast_errors.getProviderCaseStatusesFailed"),
        );
        console.error("Error fetching provider case statuses:", error);
        return throwError(() => error);
      }),
    );
  }

  /** Avoid provider_case_status while first-time onboarding is in progress. */
  private shouldSkipStatusFetch(): boolean {
    return isUserOnboardingInProgress(this.profileService.userProfile());
  }
}
